import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import NewsCard from '@/components/feature/NewsCard';
import { news } from '@/mocks/news';

const CATEGORIES = ['新闻', '评论', '专题报道', '新番', '资讯'];
const PER_PAGE = 4;

export default function NewsCategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const category = searchParams.get('category') || '';
  const order = searchParams.get('order') || 'desc';
  const page = Number(searchParams.get('page')) || 1;

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  const filtered = useMemo(() => {
    const list = news.filter((item) => (category ? item.category === category : true));
    list.sort((a, b) => {
      const result = a.date < b.date ? -1 : 1;
      return order === 'desc' ? -result : result;
    });
    return list;
  }, [category, order]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const categoryCount = (name: string) => news.filter((item) => item.category === name).length;

  const categoryFilter = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setParam('category', '')}
        className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm font-label transition-colors ${
          !category ? 'bg-primary-100 text-primary-800' : 'text-foreground-600 hover:bg-background-100'
        }`}
      >
        全部资讯
        <span className="text-xs text-foreground-400">{news.length}</span>
      </button>
      {CATEGORIES.map((name) => (
        <button
          key={name}
          type="button"
          onClick={() => setParam('category', name === category ? '' : name)}
          className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm font-label transition-colors ${
            name === category ? 'bg-primary-100 text-primary-800' : 'text-foreground-600 hover:bg-background-100'
          }`}
        >
          {name}
          <span className="text-xs text-foreground-400">{categoryCount(name)}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <title>动漫资讯 - 漫蛙动漫频道</title>
      <meta
        name="description"
        content="漫蛙动漫资讯频道，汇总最新动漫新闻、作品评论与专题报道，及时了解动画新番动态与行业观察。"
      />
      <meta name="keywords" content="漫蛙,动漫资讯,动漫新闻,新番,动漫评论,专题报道" />

      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '动漫资讯' }]} />
            <h1 className="mt-3 font-heading text-2xl font-800 text-foreground-950 md:text-3xl">动漫资讯</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              漫画与动画领域的最新动态、深度评论与专题报道，帮你及时掌握行业脉搏、发现值得一看的作品。
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
            <aside className="hidden lg:block">
              <div className="sticky top-24 flex flex-col gap-6">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-800 text-foreground-950">
                    <i className="ri-filter-3-line text-primary-600" />
                    资讯类型
                  </h2>
                  {categoryFilter}
                </div>
                <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                  <h2 className="mb-3 font-heading text-base font-800 text-foreground-950">热门推荐</h2>
                  <div className="flex flex-col gap-2">
                    {news.slice(2, 6).map((item) => (
                      <NewsCard key={item.id} item={item} layout="compact" />
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <div>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setMobileFilterOpen((v) => !v)}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-label text-foreground-700 lg:hidden"
                  >
                    <i className="ri-filter-3-line" />
                    筛选
                  </button>
                  <span className="text-sm font-label text-foreground-500">
                    共 <strong className="font-700 text-primary-700">{filtered.length}</strong> 篇资讯
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setParam('order', 'desc')}
                    className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label transition-colors ${
                      order === 'desc' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'
                    }`}
                  >
                    最新优先
                  </button>
                  <button
                    type="button"
                    onClick={() => setParam('order', 'asc')}
                    className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label transition-colors ${
                      order === 'asc' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'
                    }`}
                  >
                    最早优先
                  </button>
                </div>
              </div>

              {mobileFilterOpen && (
                <div className="mb-5 rounded-lg border border-background-200 bg-background-50 p-5 lg:hidden">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-heading text-base font-700 text-foreground-950">资讯类型</span>
                    <button
                      type="button"
                      onClick={() => setMobileFilterOpen(false)}
                      aria-label="关闭筛选"
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                  {categoryFilter}
                </div>
              )}

              {pageItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                  <span className="mw-float flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
                    <i className="ri-newspaper-line" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-700 text-foreground-950">暂无符合筛选条件的资讯</h3>
                  <p className="mt-2 text-sm font-label text-foreground-500">换一个资讯类型试试吧</p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-500 text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置筛选
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {pageItems.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>
              )}

              <Pagination page={safePage} totalPages={totalPages} onChange={(p) => setParam('page', String(p))} />
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}